import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n';
import { useTheme } from '@/themes/themes';
import GoogleSignInButton, { AuthDivider } from '@/components/GoogleSignInButton';

const BG_STYLE = {
  backgroundImage: `
    radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.12) 100%),
    radial-gradient(circle at center, rgba(255,250,235,0.2) 0%, rgba(255,250,235,0.08) 25%, transparent 50%),
    url(/assets/havn_bg_dark.png)
  `,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
};

const inputStyle = {
  width: '100%', padding: '14px 16px', fontSize: '16px',
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '10px', color: '#fff', outline: 'none',
  transition: 'border-color 0.2s', boxSizing: 'border-box',
};

const selectStyle = {
  ...inputStyle,
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 14px center',
  paddingRight: '36px',
};

const labelStyle = {
  display: 'block', fontSize: '13px', fontWeight: 600,
  color: 'rgba(255,255,255,0.6)', marginBottom: '6px',
};

const eyeBtnStyle = {
  position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
  color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center',
};

const focusHandler = e => e.target.style.borderColor = 'var(--havn-primary-20)';
const blurHandler = e => e.target.style.borderColor = 'rgba(255,255,255,0.12)';

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function SignupPage() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const { t, lang, setLang, languages } = useTranslation();
  const { theme, themeName } = useTheme();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [referral, setReferral] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const ageRangeKeys = ['age_under_18','age_18_29','age_30_39','age_40_49','age_50_59','age_60_69','age_70_79','age_80_plus'];
  const referralKeys = ['referral_family','referral_friend','referral_bank','referral_caregiver','referral_social','referral_news','referral_other'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!firstName.trim() || !lastName.trim()) { setError(t('error_name_required')); return; }
    if (!ageRange) { setError(t('error_age_required')); return; }
    if (phone) {
      const digits = phone.replace(/\D/g, '');
      if (digits.length > 0 && digits.length < 10) { setError(t('error_phone_invalid')); return; }
    }
    if (password.length < 8) { setError(t('error_password_short')); return; }
    if (password !== confirmPassword) { setError(t('error_password_mismatch')); return; }

    setLoading(true);
    try {
      const phoneDigits = phone.replace(/\D/g, '');
      const fullPhone = phoneDigits ? `+1${phoneDigits}` : '';

      await signUp(email, password, {
        full_name: `${firstName.trim()} ${lastName.trim()}`,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: fullPhone,
        age_range: ageRange,
        referral_source: referral,
        language: lang,
      });
      localStorage.setItem('havn_first_run', 'pending');
      navigate('/welcome', { replace: true });
    } catch (err) {
      setError(err.message || t('signup_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...BG_STYLE, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', backgroundColor: theme.background, backgroundImage: `radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.12) 100%), url(${theme.backgroundImage || '/assets/Smooth_blue_gradient_backdrop.png'})` }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          width: '100%', maxWidth: '440px',
          background: 'var(--havn-surface)', backdropFilter: 'blur(20px)',
          borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)',
          padding: '36px 28px',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <img src="/havn-logo-white.png" alt="HAVN" style={{ height: '32px', display: 'block', margin: '0 auto 24px' }} />

        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#fff', textAlign: 'center', marginBottom: '6px' }}>{t('signup_title')}</h2>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginBottom: '24px' }}>{t('signup_subtitle')}</p>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#F87171' }}>
            {error}
          </div>
        )}

        <GoogleSignInButton
          label={lang === 'fr' ? 'Continuer avec Google' : 'Continue with Google'}
          onError={setError}
        />
        <AuthDivider label={lang === 'fr' ? 'ou' : 'or'} />

        <form onSubmit={handleSubmit}>
          {/* Language selector — first field */}
          <div style={{ marginBottom: '18px' }}>
            <label style={labelStyle}>{t('label_language')}</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              {languages.map(l => (
                <button key={l.code} type="button" onClick={() => setLang(l.code)}
                  style={{
                    flex: 1, padding: '12px', fontSize: '15px', fontWeight: 600,
                    background: lang === l.code ? 'var(--havn-primary-10)' : 'rgba(255,255,255,0.04)',
                    border: `2px solid ${lang === l.code ? 'var(--havn-primary)' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '10px', cursor: 'pointer',
                    color: lang === l.code ? 'var(--havn-primary)' : 'rgba(255,255,255,0.5)',
                    transition: 'all 0.15s',
                  }}>
                  {l.flag} {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* Name row */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('label_first_name')} {t('label_required')}</label>
              <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
                placeholder={t('placeholder_first_name')} required autoComplete="given-name" autoFocus
                style={inputStyle} onFocus={focusHandler} onBlur={blurHandler} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('label_last_name')} {t('label_required')}</label>
              <input type="text" value={lastName} onChange={e => setLastName(e.target.value)}
                placeholder={t('placeholder_last_name')} required autoComplete="family-name"
                style={inputStyle} onFocus={focusHandler} onBlur={blurHandler} />
            </div>
          </div>

          {/* Email */}
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>{t('label_email')} {t('label_required')}</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder={t('placeholder_email')} required autoComplete="email"
              style={inputStyle} onFocus={focusHandler} onBlur={blurHandler} />
          </div>

          {/* Phone */}
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>{t('label_phone')} <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.35)' }}>{t('label_optional')}</span></label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ ...inputStyle, width: '60px', flexShrink: 0, textAlign: 'center', padding: '14px 8px', color: 'rgba(255,255,255,0.5)', cursor: 'default' }}>{t('phone_country')}</div>
              <input type="tel" value={phone} onChange={e => setPhone(formatPhone(e.target.value))}
                placeholder={t('placeholder_phone')} autoComplete="tel-national"
                style={inputStyle} onFocus={focusHandler} onBlur={blurHandler} />
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>{t('phone_note')}</div>
          </div>

          {/* Age range */}
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>{t('label_age_range')} {t('label_required')}</label>
            <select value={ageRange} onChange={e => setAgeRange(e.target.value)} required
              style={{ ...selectStyle, color: ageRange ? '#fff' : 'rgba(255,255,255,0.35)' }}
              onFocus={focusHandler} onBlur={blurHandler}>
              <option value="" disabled>{t('placeholder_age_range')}</option>
              {ageRangeKeys.map(k => <option key={k} value={t(k)} style={{ background: '#0d1f3c', color: '#fff' }}>{t(k)}</option>)}
            </select>
          </div>

          {/* Referral */}
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>{t('label_referral')} <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.35)' }}>{t('label_optional')}</span></label>
            <select value={referral} onChange={e => setReferral(e.target.value)}
              style={{ ...selectStyle, color: referral ? '#fff' : 'rgba(255,255,255,0.35)' }}
              onFocus={focusHandler} onBlur={blurHandler}>
              <option value="">{t('placeholder_referral')}</option>
              {referralKeys.map(k => <option key={k} value={t(k)} style={{ background: '#0d1f3c', color: '#fff' }}>{t(k)}</option>)}
            </select>
          </div>

          {/* Password */}
          <div style={{ marginBottom: '14px' }}>
            <label style={labelStyle}>{t('label_password')} {t('label_required')}</label>
            <div style={{ position: 'relative' }}>
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                placeholder={t('placeholder_password')} required autoComplete="new-password"
                style={{ ...inputStyle, paddingRight: '48px' }} onFocus={focusHandler} onBlur={blurHandler} />
              <button type="button" onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? t('aria_hide_password') : t('aria_show_password')} style={eyeBtnStyle}>
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Confirm password */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>{t('label_confirm_password')} {t('label_required')}</label>
            <div style={{ position: 'relative' }}>
              <input type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                placeholder={t('placeholder_confirm_password')} required autoComplete="new-password"
                style={{ ...inputStyle, paddingRight: '48px' }} onFocus={focusHandler} onBlur={blurHandler} />
              <button type="button" onClick={() => setShowConfirm(v => !v)}
                aria-label={showConfirm ? t('aria_hide_password') : t('aria_show_password')} style={eyeBtnStyle}>
                {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading}
            style={{
              width: '100%', padding: '14px', fontSize: '15px', fontWeight: 600,
              background: loading ? 'var(--havn-primary-20)' : 'var(--havn-primary)',
              color: '#000', border: 'none', borderRadius: '10px', cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}>
            {loading ? t('signup_loading') : t('signup_btn')}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>
          {t('signup_has_account')}{' '}
          <Link to="/login" style={{ color: 'var(--havn-primary)', textDecoration: 'none', fontWeight: 600 }}>{t('signup_sign_in')}</Link>
        </p>
      </motion.div>
    </div>
  );
}
